import Swiper, {Pagination, Navigation, Keyboard, EffectFade} from "swiper";
import {isValidTel, isValidMail} from "@main/helpers/is-valid-field";
import focusManager from "focus-manager";
import {ChoicesFactory} from "@main/factories/ChoicesFactory";
import {ToggleScroll} from "@main/components/toggle-scroll";

const INDEX_FIRST_SLIDE = 0;

/** Класс модального окна с анкетой. */
class InternationalModal {
    swiper
    
    /**
     * Конструктор класса.
     *
     * @param {HTMLElement} el Элемент.
     */
    constructor(el) {
        this.el = el;
        this.documentEl = this.el.closest("body");
        this.modal = this.documentEl.querySelector(".js-modal-international");
        this.form = this.modal.querySelector(".js-form-international");
        this.slider = this.modal.querySelector(".js-swiper-international");
        this.openModalButton = this.el.querySelector(".js-open-modal-international");
        this.sectionSelects = this.form.querySelectorAll(".js-international-select");
        this.sectionSelectBlocks = this.form.querySelectorAll(".js-select-block");
        this.addLanguageBtn = this.form.querySelector(".js-add-language");

        this.sectionSelects.forEach(select => {
            ChoicesFactory.create(select);
        })

        this.createSlider();
        this.listenEvents();
    }

    /**
     * Прослушивание событий.
     * 
     * @returns {void}
     */
    listenEvents() {
        this.openModalButton.addEventListener("click", () => {
            this.modal.classList.add("shown");
            ToggleScroll.disable(this.modal);
            focusManager.capture(this.modal);
            this.swiper.slideTo(0, 0);
            this.validationSlideForm();

        })

        this.form.addEventListener("click", (e) => {
            if(e.target.type === "radio") {
                this.checkRadio(e);
            }
        })
        
        this.swiper.on("activeIndexChange", () => {
            this.activeIndex = this.swiper.activeIndex;
            this.activeSlide = this.swiper.slides[this.activeIndex];
            this.inputs = this.activeSlide.querySelectorAll("input");

            this.changeNavigationLocation();            
            this.showBtnSubmitOnLastSlide();
            this.validationOnInput();
        })

        this.swiper.navigation.nextEl.addEventListener("click", () => {
            this.validationSlideForm();
            this.scrollIntoView();
        })

        this.addLanguageBtn.addEventListener("click", () => {
            this.addLanguages();
            this.swiper.updateAutoHeight(150);
            this.swiper.update();
        })

        this.validationOnInput();
    }

    /**
     * При переключении слайдов, прокручиваем окно к его началу
     *
     * @returns {void}
     */
    scrollIntoView() {
        this.modal.scrollTop = 0;
    }

    /**
     * Инпут для ввода доп информации становится видимым,
     * если пользователь выбрал ответ "Да"
     *
     * @param {Event} e Событие, происходящее при клике по input type radio
     * @returns {void}
     */

    checkRadio(e) {
        const target = e.target;
        const row = target.closest(".js-question-item");
        if(target.value === "yes") {
            row.classList.add("input-is-visible");
        } else {
            row.classList.remove("input-is-visible");
        }
    }

    /**
     * Добавить блок с ещё одним языком
     *
     * @returns {void}
     */
    addLanguages() {
        const blocksArr = Array.from(this.sectionSelectBlocks);
        const hiddenBlocks = blocksArr.filter(item => item.classList.contains("hidden"));
        const hiddenSelectBlock = blocksArr.find(block => {
            return block.classList.contains("hidden");
        })

        if(hiddenSelectBlock) {
            hiddenSelectBlock.classList.remove("hidden");
        }

        if(hiddenBlocks.length <= 1) {
            this.addLanguageBtn.setAttribute("disabled", "disabled");
        }
    }

    /**
     * Валидация при вводе в поле.
     * 
     * @returns {void}
     */
    validationOnInput() {
        this.inputs.forEach(input => {            
            input.addEventListener("input", () => {
                if (input.classList.contains("js-number-input")) {
                    input.value = input.value.replace(/[^\d]/g,"");
                }
                
                this.validationSlideForm();                
            })
        })
    }

    /**
     * Валидация полей активного слайда.
     * 
     * @returns {void}
     */
    validationSlideForm() {
        const activeIndex = this.swiper.activeIndex;
        const activeSlide = this.swiper.slides[activeIndex];
        this.requiredNodes = activeSlide.querySelectorAll("[required]");
        this.inputs = activeSlide.querySelectorAll("input");

        let isValid = true;

        this.requiredNodes.forEach(node => {

            if(node.type === "tel") {
                if(!isValidTel(node) || !(node.value.trim().length > 0)) {
                    isValid = false;
                    return
                }
            } else if (node.type === "email") {
                if(!isValidMail(node) || !(node.value.trim().length > 0)) {
                    isValid = false;
                    return
                }
            }  else if (node.type === "checkbox") {
                if(!node.checked) {
                    isValid = false;
                    return
                }
            } else {
                if(!node.value.trim().length) {
                    isValid = false;
                    return
                }
            }
        });

        if (isValid) {
            this.unblockNavigationNext();
            if (this.swiper.activeIndex === this.lastSlideIndex) {
                this.modal.querySelector(".js-form-submit-btn").removeAttribute("disabled");
            } 
        } else {
            this.blockNavigationNext();
            if (this.swiper.activeIndex === this.lastSlideIndex) {
                this.modal.querySelector(".js-form-submit-btn").setAttribute("disabled", "disabled");
            } 
        }

        this.swiper.updateAutoHeight(0);
    }

    /**
     * Инициализация слайдера.
     * 
     * @returns {void} 
     */
    createSlider() {
        this.swiper = new Swiper(this.slider, {
            slidesPerView: 1,
            modules: [Pagination, Navigation, Keyboard, EffectFade],
            autoHeight: true,
            allowTouchMove: false,
            observeParents: true,
            observeSlideChildren: true,
            observer: true,
            keyboard: {
                enabled: true,
            },
            effect: "fade",
            fadeEffect: {
                crossFade: true,
            },
            navigation: {
                nextEl: ".js-recruitment-next",
                prevEl: ".js-recruitment-prev"
            },
            pagination: {
                el: ".js-recruitment-pagination",
                clickable: false,
                renderBullet: (index, className) => {
                    return "<span class=\"" + className + "\">" + (index + 1) + "</span>";
                },
            },
        });

        this.activeIndex = this.swiper.activeIndex;
        this.activeSlide = this.swiper.slides[this.activeIndex];
        this.inputs = this.activeSlide.querySelectorAll("input");
        this.lastSlideIndex = this.swiper.slides.length - 1;

    }

    /**
     * Изменение расположения навигационных кнопок в зависимости от активного слайда.
     * 
     * @returns {void} 
     */
    changeNavigationLocation() {
        if (this.swiper.activeIndex !== INDEX_FIRST_SLIDE) {
            this.swiper.el.classList.add("first-slide-active");
        } else {
            this.swiper.el.classList.remove("first-slide-active");
        }
    }

    /**
     * Действия при переходе к последнему слайду.
     * 
     * @returns {void} 
     */
    showBtnSubmitOnLastSlide() {
        if (this.swiper.activeIndex === this.lastSlideIndex) {
            this.swiper.el.classList.add("last-slide-active");
        } else {
            this.swiper.el.classList.remove("last-slide-active");
        }
    }

    /**
     * Блокировать кнопку навигации "Далее",
     * если не все обязательные поля на слайде прошли валидацию
     *
     * @returns {void}
     */
    blockNavigationNext() {
        this.swiper.navigation.nextEl.classList.add("swiper-button-disabled");
    }

    /**
     * Разблокировать кнопку навигации "Далее".
     *
     * @returns {void}
     */
    unblockNavigationNext() {
        this.swiper.navigation.nextEl.classList.remove("swiper-button-disabled");
    }
}

window.addEventListener("load", () => {
    document.querySelectorAll(".js-international").forEach((item) => new InternationalModal(item));
});
